import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Space & Astronomy — 8 templates
   rocket_launch, satellite_orbit, galaxy_spiral, moon_phases,
   constellation_map, star_lifecycle, telescope_view, asteroid_belt
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Rocket Launch ────────────────────────────────────── */
export const RocketLaunch: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Rocket Launch";
  const animationProgress = (props.animationProgress as number) || 0;
  const accentColor = (props.accentColor as string) || "#e74c3c";
  const rocketY = 400 - animationProgress * 300;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="rl-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a0a2e" />
          <stop offset={`${60 + animationProgress * 40}%`} stopColor="#1a237e" />
          <stop offset="100%" stopColor="#283593" />
        </linearGradient>
        <radialGradient id="rl-flame" cx="50%" cy="0%" r="80%">
          <stop offset="0%" stopColor="#fff" /><stop offset="30%" stopColor="#ff9800" />
          <stop offset="100%" stopColor={accentColor} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="url(#rl-sky)" rx="16" />
      {/* Stars */}
      {Array.from({ length: 40 }, (_, i) => (
        <circle key={i} cx={50 + (i * 197) % 700} cy={30 + (i * 131) % 560}
          r={0.5 + (i % 3) * 0.5} fill="#fff" opacity={0.3 + (i % 4) * 0.2} />
      ))}
      {/* Ground */}
      <rect x="0" y="520" width="800" height="80" fill="#1b5e20" rx="0" />
      {/* Launch pad */}
      <rect x="360" y="510" width="80" height="15" rx="3" fill="#546e7a" />
      {/* Rocket */}
      <g transform={`translate(400, ${rocketY})`}>
        {/* Flame */}
        {animationProgress > 0 && (
          <ellipse cx="0" cy={60 + animationProgress * 20} rx={15 + animationProgress * 10}
            ry={30 + animationProgress * 30} fill="url(#rl-flame)" opacity="0.8" />
        )}
        {/* Body */}
        <rect x="-16" y="-40" width="32" height="80" rx="4" fill="#ecf0f1" />
        {/* Nose cone */}
        <polygon points="-16,-40 0,-80 16,-40" fill={accentColor} />
        {/* Window */}
        <circle cx="0" cy="-10" r="8" fill="#1a237e" stroke="#bdbdbd" strokeWidth="1.5" />
        {/* Fins */}
        <polygon points="-16,30 -30,50 -16,40" fill={accentColor} />
        <polygon points="16,30 30,50 16,40" fill={accentColor} />
      </g>
      {/* Smoke trail */}
      {animationProgress > 0.1 && (
        <g opacity={animationProgress * 0.5}>
          {[0, 1, 2, 3, 4].map((i) => (
            <ellipse key={i} cx={400 + (i % 2 === 0 ? -1 : 1) * i * 8}
              cy={rocketY + 80 + i * 40} rx={10 + i * 8} ry={8 + i * 5}
              fill="#bdbdbd" opacity={0.3 - i * 0.05} />
          ))}
        </g>
      )}
      <text x="400" y="580" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Satellite Orbit ──────────────────────────────────── */
export const SatelliteOrbit: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Satellite Orbit";
  const rotation = (props.rotation as number) || 0;
  const accentColor = (props.accentColor as string) || "#3498db";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <radialGradient id="so-earth" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#2196f3" /><stop offset="100%" stopColor="#0d47a1" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="#0a0a1e" rx="16" />
      {Array.from({ length: 30 }, (_, i) => (
        <circle key={i} cx={40 + (i * 257) % 720} cy={30 + (i * 191) % 540} r={0.8 + (i % 3) * 0.4} fill="#fff" opacity={0.3 + (i % 4) * 0.15} />
      ))}
      {/* Earth */}
      <circle cx="400" cy="300" r="100" fill="url(#so-earth)" />
      <path d="M340,260 Q380,240 400,270 Q420,300 380,310 Q350,300 340,260Z" fill="#2e7d32" opacity="0.5" />
      <path d="M420,260 Q460,250 470,280 Q475,310 440,300Z" fill="#2e7d32" opacity="0.4" />
      {/* Orbit path */}
      <ellipse cx="400" cy="300" rx="220" ry="80" fill="none" stroke="#546e7a" strokeWidth="1.5" strokeDasharray="8,4"
        transform="rotate(-20, 400, 300)" />
      {/* Satellite */}
      <g transform={`rotate(${rotation}, 400, 300)`}>
        <g transform="translate(620, 300) rotate(20)">
          {/* Body */}
          <rect x="-10" y="-8" width="20" height="16" rx="2" fill="#bdbdbd" stroke="#757575" strokeWidth="1" />
          {/* Solar panels */}
          <rect x="-40" y="-4" width="28" height="8" rx="1" fill={accentColor} opacity="0.8" />
          <rect x="12" y="-4" width="28" height="8" rx="1" fill={accentColor} opacity="0.8" />
          {/* Antenna */}
          <line x1="0" y1="-8" x2="0" y2="-18" stroke="#bdbdbd" strokeWidth="1.5" />
          <circle cx="0" cy="-20" r="3" fill="#e0e0e0" />
        </g>
      </g>
      {/* Signal waves */}
      {[1, 2, 3].map((i) => (
        <circle key={i} cx="400" cy="300" r={100 + i * 40} fill="none"
          stroke={accentColor} strokeWidth="0.8" opacity={0.3 - i * 0.08}
          strokeDasharray="6,8" />
      ))}
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Galaxy Spiral ────────────────────────────────────── */
export const GalaxySpiral: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Galaxy";
  const rotation = (props.rotation as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <radialGradient id="gx-core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff9c4" /><stop offset="40%" stopColor="#f9a825" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#f9a825" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="#050510" rx="16" />
      {Array.from({ length: 60 }, (_, i) => (
        <circle key={i} cx={30 + (i * 137) % 740} cy={20 + (i * 97) % 560} r={0.5 + (i % 4) * 0.3} fill="#fff" opacity={0.2 + (i % 5) * 0.1} />
      ))}
      <g transform={`rotate(${rotation * 0.3}, 400, 300)`}>
        {/* Spiral arms */}
        {[0, 120, 240].map((offset) => (
          <g key={offset} transform={`rotate(${offset}, 400, 300)`}>
            <path d={`M400,300 Q450,250 500,240 Q560,230 600,260 Q640,300 620,350`}
              fill="none" stroke="#7c4dff" strokeWidth="30" opacity="0.15" strokeLinecap="round" />
            <path d={`M400,300 Q450,250 500,240 Q560,230 600,260 Q640,300 620,350`}
              fill="none" stroke="#b388ff" strokeWidth="6" opacity="0.3" strokeLinecap="round" />
            {/* Stars along arm */}
            {[1, 2, 3, 4, 5].map((s) => (
              <circle key={s} cx={400 + s * 45} cy={300 - s * 10 + Math.sin(s) * 15}
                r={1 + Math.random()} fill="#e1bee7" opacity={0.4 + s * 0.08} />
            ))}
          </g>
        ))}
        {/* Core */}
        <circle cx="400" cy="300" r="60" fill="url(#gx-core)" />
        <circle cx="400" cy="300" r="15" fill="#fff9c4" opacity="0.8" />
      </g>
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Moon Phases ──────────────────────────────────────── */
export const MoonPhases: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Moon Phases";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const phases = (props.phases as string[]) || ["New", "Waxing Crescent", "First Quarter", "Waxing Gibbous", "Full", "Waning Gibbous", "Last Quarter", "Waning Crescent"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0a0a1e" rx="16" />
      {Array.from({ length: 30 }, (_, i) => (
        <circle key={i} cx={40 + (i * 193) % 720} cy={30 + (i * 127) % 200} r={0.6} fill="#fff" opacity={0.3} />
      ))}
      {/* 8 moon phases */}
      {phases.map((p, i) => {
        const x = 80 + i * 85;
        const y = 280;
        const r = 32;
        // Shadow coverage: 0=full shadow, 4=full light
        const shadowWidth = Math.abs(i - 4) * (r / 4);
        const shadowSide = i < 4 ? "right" : "left";
        return (
          <g key={i} opacity={i === activeIndex ? 1 : 0.7}>
            {/* Moon */}
            <circle cx={x} cy={y} r={r} fill="#e0e0e0"
              stroke={i === activeIndex ? "#fff" : "#546e7a"} strokeWidth={i === activeIndex ? 2.5 : 1} />
            {/* Shadow */}
            {i !== 4 && (
              <clipPath id={`mp-clip-${i}`}><circle cx={x} cy={y} r={r} /></clipPath>
            )}
            {i === 0 && <circle cx={x} cy={y} r={r} fill="#1a1a2e" />}
            {i > 0 && i < 4 && (
              <rect x={x + r - shadowWidth * 2} y={y - r} width={shadowWidth * 2} height={r * 2}
                fill="#1a1a2e" clipPath={`url(#mp-clip-${i})`} />
            )}
            {i > 4 && (
              <rect x={x - r} y={y - r} width={shadowWidth * 2} height={r * 2}
                fill="#1a1a2e" clipPath={`url(#mp-clip-${i})`} />
            )}
            {/* Label */}
            <text x={x} y={y + 55} textAnchor="middle" fill={i === activeIndex ? "#fff" : "#bdc3c7"}
              fontSize="10" fontWeight="500">{p}</text>
          </g>
        );
      })}
      <text x="400" y="440" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Constellation Map ────────────────────────────────── */
export const ConstellationMap: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Constellation";
  const revealCount = (props.revealCount as number) ?? 99;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#050515" rx="16" />
      {/* Background stars */}
      {Array.from({ length: 50 }, (_, i) => (
        <circle key={i} cx={40 + (i * 163) % 720} cy={30 + (i * 109) % 540} r={0.5 + (i % 3) * 0.3} fill="#fff" opacity={0.15 + (i % 5) * 0.05} />
      ))}
      {/* Constellation stars & lines (Big Dipper pattern) */}
      {[
        { x: 200, y: 200 }, { x: 280, y: 180 }, { x: 360, y: 190 }, { x: 420, y: 220 },
        { x: 460, y: 300 }, { x: 540, y: 320 }, { x: 600, y: 280 },
      ].map((s, i) => (
        <g key={i} opacity={i < revealCount ? 1 : 0.15}>
          <circle cx={s.x} cy={s.y} r="5" fill="#fff" />
          <circle cx={s.x} cy={s.y} r="12" fill="#fff" opacity="0.1" />
        </g>
      ))}
      {/* Lines */}
      {[
        [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6],
      ].map(([a, b], i) => {
        const stars = [
          { x: 200, y: 200 }, { x: 280, y: 180 }, { x: 360, y: 190 }, { x: 420, y: 220 },
          { x: 460, y: 300 }, { x: 540, y: 320 }, { x: 600, y: 280 },
        ];
        return (
          <line key={i} x1={stars[a].x} y1={stars[a].y} x2={stars[b].x} y2={stars[b].y}
            stroke="#7c4dff" strokeWidth="1.5" opacity={Math.min(a, b) < revealCount - 1 ? 0.6 : 0.1} />
        );
      })}
      <text x="400" y="500" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Star Lifecycle ───────────────────────────────────── */
export const StarLifecycle: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Star Lifecycle";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const stages = (props.stages as string[]) || ["Nebula", "Protostar", "Main Sequence", "Red Giant", "Supernova"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#050515" rx="16" />
      {/* Stages along an arrow path */}
      {[
        { x: 100, r: 35, c: "#7c4dff" }, { x: 230, r: 30, c: "#ff9800" }, { x: 380, r: 40, c: "#ffeb3b" },
        { x: 530, r: 55, c: "#e53935" }, { x: 680, r: 25, c: "#fff" },
      ].map((s, i) => (
        <g key={i}>
          {/* Arrow between */}
          {i < 4 && <line x1={s.x + s.r + 5} y1="280" x2={[230, 380, 530, 680][i] - [30, 40, 55, 25][i] - 5} y2="280"
            stroke="#546e7a" strokeWidth="2" markerEnd="url(#sl-arrow)" />}
          {/* Star */}
          <circle cx={s.x} cy="280" r={s.r}
            fill={i === activeIndex ? "#fff" : s.c}
            opacity={i === activeIndex ? 1 : 0.7}
            stroke={i === activeIndex ? "#fff" : "none"} strokeWidth="2" />
          {/* Glow */}
          <circle cx={s.x} cy="280" r={s.r + 15} fill={s.c} opacity={i === activeIndex ? 0.15 : 0.05} />
          <text x={s.x} y={280 + s.r + 30} textAnchor="middle"
            fill={i === activeIndex ? "#fff" : "#bdc3c7"} fontSize="12" fontWeight="500">
            {stages[i] || `Stage ${i + 1}`}
          </text>
        </g>
      ))}
      <defs>
        <marker id="sl-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10Z" fill="#546e7a" />
        </marker>
      </defs>
      <text x="400" y="440" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Telescope View ───────────────────────────────────── */
export const TelescopeView: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Through the Telescope";
  const accentColor = (props.accentColor as string) || "#7c4dff";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <radialGradient id="tv-vignette" cx="50%" cy="50%" r="50%">
          <stop offset="60%" stopColor="#0a0a1e" stopOpacity="0" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.9" />
        </radialGradient>
        <clipPath id="tv-lens"><circle cx="400" cy="280" r="200" /></clipPath>
      </defs>
      <rect width="800" height="600" fill="#000" rx="16" />
      {/* Telescope view circle */}
      <g clipPath="url(#tv-lens)">
        <rect x="200" y="80" width="400" height="400" fill="#050520" />
        {/* Nebula */}
        <ellipse cx="350" cy="260" rx="80" ry="60" fill={accentColor} opacity="0.12" />
        <ellipse cx="420" cy="290" rx="60" ry="45" fill="#e91e63" opacity="0.08" />
        {/* Stars through telescope */}
        {Array.from({ length: 25 }, (_, i) => (
          <circle key={i} cx={250 + (i * 73) % 300} cy={120 + (i * 53) % 320}
            r={0.8 + (i % 4) * 0.6} fill="#fff" opacity={0.4 + (i % 3) * 0.2} />
        ))}
        {/* Planet */}
        <circle cx="440" cy="240" r="18" fill="#e65100" opacity="0.7" />
        <ellipse cx="440" cy="240" rx="30" ry="6" fill="none" stroke="#ffcc80" strokeWidth="2" opacity="0.5" />
      </g>
      {/* Vignette (circular mask edge) */}
      <circle cx="400" cy="280" r="200" fill="url(#tv-vignette)" />
      <circle cx="400" cy="280" r="200" fill="none" stroke="#1a1a1a" strokeWidth="8" />
      {/* Crosshair */}
      <line x1="400" y1="170" x2="400" y2="390" stroke="#546e7a" strokeWidth="0.5" opacity="0.5" />
      <line x1="290" y1="280" x2="510" y2="280" stroke="#546e7a" strokeWidth="0.5" opacity="0.5" />
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Asteroid Belt ────────────────────────────────────── */
export const AsteroidBelt: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Asteroid Belt";
  const rotation = (props.rotation as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <radialGradient id="ab-sun" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff9c4" /><stop offset="100%" stopColor="#f9a825" />
        </radialGradient>
      </defs>
      <rect width="800" height="600" fill="#050510" rx="16" />
      {Array.from({ length: 20 }, (_, i) => (
        <circle key={i} cx={50 + (i * 203) % 700} cy={30 + (i * 131) % 540} r={0.5} fill="#fff" opacity={0.2} />
      ))}
      {/* Sun */}
      <circle cx="400" cy="300" r="40" fill="url(#ab-sun)" />
      {/* Inner orbit */}
      <ellipse cx="400" cy="300" rx="120" ry="50" fill="none" stroke="#546e7a" strokeWidth="0.8"
        transform="rotate(-10, 400, 300)" />
      {/* Asteroid belt */}
      <g transform={`rotate(${rotation * 0.2}, 400, 300)`}>
        {Array.from({ length: 40 }, (_, i) => {
          const angle = (i * 9 + rotation) * Math.PI / 180;
          const r = 180 + (i % 5) * 15;
          const ry = r * 0.35;
          return (
            <ellipse key={i} cx={400 + Math.cos(angle) * r} cy={300 + Math.sin(angle) * ry}
              rx={2 + (i % 3)} ry={1.5 + (i % 2)} fill="#9e9e9e" opacity={0.4 + (i % 3) * 0.15}
              transform={`rotate(${i * 23}, ${400 + Math.cos(angle) * r}, ${300 + Math.sin(angle) * ry})`} />
          );
        })}
      </g>
      {/* Outer orbit */}
      <ellipse cx="400" cy="300" rx="300" ry="110" fill="none" stroke="#546e7a" strokeWidth="0.8"
        transform="rotate(-10, 400, 300)" />
      <text x="400" y="500" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
